import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Papa from "papaparse";
import CommonTable from "../CommonTable/CommonTable";
import {
  getValidationRules,
  getComparisonRules,
  getHistoricalRules,
  getCausalityRules,
  deleteValidationRule,
  deleteComparisonRule,
  deleteHistoricalRule,
  deleteCausalityRule,
  activateRule,
  deactivateRule,
  postValidationRule,
  postComparisonRule,
  postCausalityRule,
  postHistoricalRule,
  updateValidationRule,
  updateComparisonRule,
  updateCausalityRule,
  updateHistoricalRule,
  startRunAllRulesTask,
  getRunAllRulesTask,
  cancelRunAllRulesTask,
} from "../../Services/RuleServices";
import {
  CausalityRule,
  ComparisonRule,
  CreateCausalityRuleRequest,
  CreateComparisonRuleRequest,
  CreateHistoricalRuleRequest,
  CreateValidationRuleRequest,
  HistoricalRule,
  JobStatus,
  ValidationRule,
  manResponseType,
} from "../../interfaces";
import {
  ComparisonTypeEnum,
  RuleStateEnum,
  ValidationRuleTypeEnum,
  PhaseDictionary,
} from "../../Enum";
import { toast } from "react-toastify";
import { associateValueToRule } from "../../Services/ChangeService";
import EditRuleModal from "../EditRuleModal/EditRuleModal";

type Props = {};

const useDebounce = <T,>(value: T, delay: number = 300): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const AllRules = (props: Props) => {
  const [rules, setRules] = useState<(ValidationRule | ComparisonRule | HistoricalRule | CausalityRule)[]>([]);
  const [filteredRules, setFilteredRules] = useState<(ValidationRule | ComparisonRule | HistoricalRule | CausalityRule)[]>([]);
  const [manifestNames, setManifestNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const [searchManifestation, setSearchManifestation] = useState<string>("");
  const [searchType, setSearchType] = useState<string>("");
  const debouncedSearchManifestation = useDebounce(searchManifestation, 300);
  const debouncedSearchType = useDebounce(searchType, 300);
  
  // Job states
  const [runningAll, setRunningAll] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [jobProgress, setJobProgress] = useState<number>(0);
  const [jobMessage, setJobMessage] = useState<string | null>(null);
  const [jobMetrics, setJobMetrics] = useState<{ generated?: number; inserted?: number }>({});
  const [polling, setPolling] = useState(false);
  
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [responseDescriptions, setResponseDescriptions] = useState<manResponseType[]>([]);
  const [idManResponseAvailable, setIdManResponseAvailable] = useState<number[]>([]);
  const [editRuleModalOpen, setEditRuleModalOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState<ValidationRule | ComparisonRule | HistoricalRule | CausalityRule | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; type: string } | null>(null);

  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  // Cargar nombres del archivo CSV
  useEffect(() => {
    const fetchManifestNames = async () => {
      try {
        const response = await fetch("/nombre_manifestaciones.csv");
        const csvText = await response.text();

        Papa.parse(csvText, {
          header: false,
          skipEmptyLines: true,
          complete: (result: any) => {
            const names: Record<string, string> = {};
            result.data.forEach((row: string[]) => {
              if (row[0] && row[1]) {
                names[row[0]] = row[1];
              }
            });
            setManifestNames(names);
          },
        });
      } catch (error) {
        console.error("Error al cargar los nombres de manifestaciones:", error);
        toast.error("Error al cargar nombres de manifestaciones");
      }
    };

    fetchManifestNames();
  }, []);

  // Cargar ID MAN RESPONSES del archivo CSV
  useEffect(() => {
    const fetchResponseDescription = async () => {
      try {
        const response = await fetch("/response_description.csv");
        const csvText = await response.text();

        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (result: any) => {
            const parsedData: manResponseType[] = [];

            result.data.forEach((row: any) => {
              if (
                row.Id &&
                row.Description &&
                row.ResponseType &&
                row.id &&
                row.description_es &&
                row.description_es !== "#ERROR!"
              ) {
                parsedData.push({
                  Id: Number(row.Id),
                  Description: row.Description,
                  ResponseType: row.ResponseType,
                  id: Number(row.id),
                  description_es: row.description_es,
                });
              }
            });
            setResponseDescriptions(parsedData);
            const uniqueIds = Array.from(new Set(parsedData.map((item) => item.Id)));
            setIdManResponseAvailable(uniqueIds);
          },
        });
      } catch (error) {
        console.error("Error al cargar response descriptions:", error);
        toast.error("Error al cargar descripciones");
      }
    };

    fetchResponseDescription();
  }, []);

  const [resolvedValues, setResolvedValues] = useState<Record<string, string>>({});

  const resolveValue = useCallback(async (
    value: string,
    variableName: string,
    applicablePhaseIds: number[]
  ): Promise<string> => {
    try {
      if (variableName.endsWith("1")) {
        const index = value.indexOf("T");
        return index !== -1 ? value.substring(0, index) : value;
      } else {
        const result = await associateValueToRule(
          value,
          variableName,
          applicablePhaseIds,
          idManResponseAvailable,
          responseDescriptions
        );

        const index = result[0].indexOf("T");
        if (value === result[0]) {
          return index !== -1 ? result[0].substring(0, index) : result[0];
        } else {
          return index !== -1 
            ? result[0].substring(0, index)
            : `${result[0]} (${value})`;
        }
      }
    } catch {
      return value;
    }
  }, [idManResponseAvailable, responseDescriptions]);

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true);
      const validationRules = await getValidationRules();
      const comparisonRules = await getComparisonRules();
      const historicalRules = await getHistoricalRules();
      const causalityRules = await getCausalityRules();

      const allRules = [
        ...validationRules,
        ...comparisonRules,
        ...historicalRules,
        ...causalityRules,
      ];

      setRules(allRules);
      setLoading(false);

      const valueMap: Record<string, string> = {};
      for (const rule of allRules) {
        if ("value" in rule && rule.value && rule.variableName && rule.applicablePhaseIds) {
          valueMap[rule.id] = await resolveValue(rule.value, rule.variableName, rule.applicablePhaseIds);
        }
        
        if ("secondValue" in rule && rule.secondValue && rule.secondVariableName && rule.applicablePhaseIds) {
          valueMap[rule.id] = await resolveValue(rule.secondValue, rule.secondVariableName, rule.applicablePhaseIds);
        }
      }
      setResolvedValues(valueMap);
    } catch (error) {
      console.error("Error fetching rules:", error);
      setError("Failed to fetch rules. Please try again later.");
      toast.error("Error al cargar reglas");
      setLoading(false);
    }
  }, [resolveValue]);

  useEffect(() => {
    if (idManResponseAvailable.length > 0) {
      fetchRules();
    }
  }, [fetchRules, idManResponseAvailable]);

  useEffect(() => {
    const filtered = rules.filter((rule) => {
      let manifestationName = "";
      if ("variableName" in rule && rule.variableName) {
        manifestationName = manifestNames[rule.variableName] || rule.variableName;
      } else if ("firstVariableName" in rule && rule.firstVariableName) {
        manifestationName = manifestNames[rule.firstVariableName] || rule.firstVariableName;
      }

      const ruleType = rule.type.slice(0, -4);

      const matchManifestation = manifestationName
        .toLowerCase()
        .includes(debouncedSearchManifestation.toLowerCase());
      const matchType = ruleType
        .toLowerCase()
        .includes(debouncedSearchType.toLowerCase());

      return matchManifestation && matchType;
    });

    setFilteredRules(filtered);
  }, [debouncedSearchManifestation, debouncedSearchType, rules, manifestNames]);

  const pollJob = useCallback(async (id: string, attempt = 0) => {
    try {
      const info = await getRunAllRulesTask(id);
      setJobStatus(info.status);
      setJobProgress(info.progress ?? 0);
      setJobMessage(info.message ?? null);
      setJobMetrics({ generated: info.generated, inserted: info.inserted });

      if (info.status === "queued" || info.status === "running") {
        const delay = Math.min(1000 * Math.pow(1.6, attempt), 10000);
        pollingTimeoutRef.current = setTimeout(() => pollJob(id, attempt + 1), delay);
      } else {
        setPolling(false);
        if (info.status === "succeeded") {
          toast.success(`✅ Reglas procesadas. Insertados: ${info.inserted ?? "?"}, Generados: ${info.generated ?? "?"}`);
          fetchRules();
        } else if (info.status === "failed") {
          toast.error(`❌ Error: ${info.message || "Falló el procesamiento"}`);
        } else if (info.status === "canceled") {
          toast.warn("⚠️ Procesamiento cancelado");
        }
      }
    } catch (error) {
      console.error("Error polling job:", error);
      const delay = Math.min(1000 * Math.pow(1.6, attempt), 10000);
      pollingTimeoutRef.current = setTimeout(() => pollJob(id, attempt + 1), delay);
    }
  }, [fetchRules]);

  const handleEditRule = useCallback((rule: any) => {
    setActionLoading(rule.id);
    setRuleToEdit(rule);
    setEditRuleModalOpen(true);
    setTimeout(() => setActionLoading(null), 500);
  }, []);

  const handleSaveRule = useCallback(async (updatedRule: any) => {
    try {
      setActionLoading(updatedRule.id);
      
      if ("ruleType" in updatedRule) {
        await updateValidationRule(updatedRule.id, updatedRule);
      } else if ("comparisonType" in updatedRule) {
        await updateComparisonRule(updatedRule.id, updatedRule);
      } else if ("firstCondition" in updatedRule) {
        await updateCausalityRule(updatedRule.id, updatedRule);
      } else {
        await updateHistoricalRule(updatedRule.id, updatedRule);
      }

      setRules((prev) =>
        prev.map((r) => (r.id === updatedRule.id ? { ...r, ...updatedRule } : r))
      );

      const newResolvedValues = { ...resolvedValues };
      
      if ("value" in updatedRule && updatedRule.value && updatedRule.variableName && updatedRule.applicablePhaseIds) {
        const resolved = await resolveValue(
          updatedRule.value, 
          updatedRule.variableName, 
          updatedRule.applicablePhaseIds
        );
        newResolvedValues[updatedRule.id] = resolved;
      }
      
      if ("secondValue" in updatedRule && updatedRule.secondValue && updatedRule.secondVariableName && updatedRule.applicablePhaseIds) {
        const resolved = await resolveValue(
          updatedRule.secondValue, 
          updatedRule.secondVariableName, 
          updatedRule.applicablePhaseIds
        );
        newResolvedValues[updatedRule.id] = resolved;
      }
      
      setResolvedValues(newResolvedValues);

      setEditRuleModalOpen(false);
      setRuleToEdit(null);
    } catch (error) {
      console.error("Error al actualizar la regla:", error);
    } finally {
      setActionLoading(null);
    }
  }, [resolvedValues, resolveValue]);

  const handleDeleteClick = useCallback((id: string, type: string) => {
    setDeleteConfirm({ id, type });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirm) return;
    
    const { id, type } = deleteConfirm;
    setActionLoading(id);
    
    try {
      switch (type) {
        case "ValidationRule":
          await deleteValidationRule(id);
          break;
        case "ComparisonRule":
          await deleteComparisonRule(id);
          break;
        case "HistoricalRule":
          await deleteHistoricalRule(id);
          break;
        case "CausalityRule":
          await deleteCausalityRule(id);
          break;
        default:
          console.error("Tipo de regla desconocido");
          return;
      }

      setRules((prevRules) => prevRules.filter((rule) => rule.id !== id));
      
      setResolvedValues((prev) => {
        const newValues = { ...prev };
        delete newValues[id];
        return newValues;
      });
      
    } catch (err) {
      console.error("Error deleting rule:", err);
    } finally {
      setActionLoading(null);
      setDeleteConfirm(null);
    }
  }, [deleteConfirm]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirm(null);
  }, []);

  const handleDuplicateRule = useCallback(async (
    rule: ValidationRule | ComparisonRule | CausalityRule | HistoricalRule
  ) => {
    setActionLoading(rule.id);
    try {
      if ("ruleType" in rule) {
        const newRule: CreateValidationRuleRequest = {
          variableName: rule.variableName,
          ruleType: rule.ruleType,
          value: rule.value,
          errorMessage: `${rule.errorMessage} (copia)`,
          ruleState: RuleStateEnum.Inactive,
          applicablePhaseIds: rule.applicablePhaseIds,
          type: rule.type,
        };
        const createdRule = await postValidationRule(newRule);
        setFilteredRules((prev) => [...prev, createdRule]);
        setRules((prev) => [...prev, createdRule]);
        
        if (createdRule.value && createdRule.variableName && createdRule.applicablePhaseIds) {
          const resolved = await resolveValue(
            createdRule.value,
            createdRule.variableName,
            createdRule.applicablePhaseIds
          );
          setResolvedValues((prev) => ({ ...prev, [createdRule.id]: resolved }));
        }
      } else if ("comparisonType" in rule) {
        const newRule: CreateComparisonRuleRequest = {
          firstVariableName: rule.firstVariableName,
          secondVariableName: rule.secondVariableName,
          comparisonType: rule.comparisonType,
          errorMessage: `${rule.errorMessage} (copia)`,
          ruleState: RuleStateEnum.Inactive,
          applicablePhaseIds: rule.applicablePhaseIds,
          type: rule.type,
        };
        const createdRule = await postComparisonRule(newRule);
        setFilteredRules((prev) => [...prev, createdRule]);
        setRules((prev) => [...prev, createdRule]);
      } else if ("firstCondition" in rule && "secondCondition" in rule) {
        const newRule: CreateCausalityRuleRequest = {
          firstVariableName: rule.firstVariableName,
          firstCondition: rule.firstCondition,
          firstValue: rule.firstValue,
          secondVariableName: rule.secondVariableName,
          secondCondition: rule.secondCondition,
          secondValue: rule.secondValue,
          useFirstVariableValue: rule.useFirstVariableValue,
          errorMessage: `${rule.errorMessage} (copia)`,
          ruleState: RuleStateEnum.Inactive,
          applicablePhaseIds: rule.applicablePhaseIds,
          type: rule.type,
        };
        const createdRule = await postCausalityRule(newRule);
        setFilteredRules((prev) => [...prev, createdRule]);
        setRules((prev) => [...prev, createdRule]);
        
        if (createdRule.secondValue && createdRule.secondVariableName && createdRule.applicablePhaseIds) {
          const resolved = await resolveValue(
            createdRule.secondValue,
            createdRule.secondVariableName,
            createdRule.applicablePhaseIds
          );
          setResolvedValues((prev) => ({ ...prev, [createdRule.id]: resolved }));
        }
      } else if ("relatedVariablesPrefix" in rule) {
        const newRule: CreateHistoricalRuleRequest = {
          variableName: rule.variableName,
          relatedVariablesPrefix: rule.relatedVariablesPrefix,
          relatedCondition: rule.relatedCondition,
          relatedValue: rule.relatedValue,
          errorMessage: `${rule.errorMessage} (copia)`,
          ruleState: RuleStateEnum.Inactive,
          applicablePhaseIds: rule.applicablePhaseIds,
          type: rule.type,
        };
        const createdRule = await postHistoricalRule(newRule);
        setFilteredRules((prev) => [...prev, createdRule]);
        setRules((prev) => [...prev, createdRule]);
      }

      toast.success("✅ Regla duplicada correctamente");
    } catch (error) {
      console.error("Error duplicando la regla:", error);
      toast.error("❌ Error al duplicar la regla");
    } finally {
      setActionLoading(null);
    }
  }, [resolveValue]);

  const handleToggleRule = useCallback(async (
    rule: ValidationRule | ComparisonRule | HistoricalRule | CausalityRule
  ) => {
    setActionLoading(rule.id);
    try {
      if (rule.ruleState === RuleStateEnum.Active) {
        await deactivateRule(rule.id, rule.type);
      } else {
        await activateRule(rule.id, rule.type);
      }

      setRules((prevRules) =>
        prevRules.map((r) =>
          r.id === rule.id
            ? {
                ...r,
                ruleState:
                  rule.ruleState === RuleStateEnum.Active
                    ? RuleStateEnum.Inactive
                    : RuleStateEnum.Active,
              }
            : r
        )
      );
    } catch (error) {
      console.error("Error cambiando estado de la regla:", error);
    } finally {
      setActionLoading(null);
    }
  }, []);

  const handleRunAll = useCallback(async () => {
    if (polling) return;
    
    setRunningAll(true);
    setJobId(null);
    setJobStatus(null);
    setJobProgress(0);
    setJobMessage(null);
    setJobMetrics({});

    try {
      const { jobId } = await startRunAllRulesTask();
      setJobId(jobId);
      setJobStatus("queued");
      setPolling(true);
      pollJob(jobId, 0);
      toast.info("🚀 Procesamiento de reglas iniciado");
    } catch (err) {
      console.error("Error starting task:", err);
      toast.error("❌ Error al iniciar el procesamiento");
    } finally {
      setRunningAll(false);
    }
  }, [polling, pollJob]);

  const handleCancelJob = useCallback(async () => {
    if (!jobId) return;
    try {
      await cancelRunAllRulesTask(jobId);
      toast.warn("⚠️ Cancelación solicitada");
    } catch {
      toast.error("❌ Error al cancelar");
    }
  }, [jobId]);

  const handleClearJobStatus = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }
    setJobId(null);
    setJobStatus(null);
    setJobProgress(0);
    setJobMessage(null);
    setJobMetrics({});
    setPolling(false);
  }, []);

  const ruletableConfig = useMemo(() => [
    {
      label: "Estado",
      render: (manifest: ValidationRule | ComparisonRule | HistoricalRule | CausalityRule) => (
        <button
          onClick={() => handleToggleRule(manifest)}
          className={`p-2 rounded center transition-all ${
            actionLoading === manifest.id ? "opacity-50 cursor-wait" : "hover:scale-110"
          }`}
          disabled={actionLoading === manifest.id}
          aria-label={manifest.ruleState === RuleStateEnum.Active ? "Desactivar regla" : "Activar regla"}
          title={manifest.ruleState === RuleStateEnum.Active ? "Desactivar" : "Activar"}
        >
          {manifest.ruleState === RuleStateEnum.Active ? "🟩" : "🟥"}
        </button>
      ),
      subTitle: "Activar o desactivar la regla",
    },
    {
      label: "Tipo",
      render: (manifest: ValidationRule | ComparisonRule | HistoricalRule | CausalityRule) => (
        <span className="font-medium">{manifest.type.slice(0, -4)}</span>
      ),
      subTitle: "Tipo de regla",
    },
    {
      label: "Primera Variable",
      render: (manifest: ValidationRule | ComparisonRule | HistoricalRule | CausalityRule) =>
        "variableName" in manifest
          ? manifestNames[manifest.variableName] || manifest.variableName
          : "firstVariableName" in manifest
          ? manifestNames[manifest.firstVariableName] || manifest.firstVariableName
          : "",
      subTitle: "Variable o primera variable",
    },
    {
      label: "Segunda Variable",
      render: (manifest: ValidationRule | ComparisonRule | HistoricalRule | CausalityRule) => {
        if ("secondVariableName" in manifest) {
          return manifestNames[manifest.secondVariableName] || manifest.secondVariableName || "";
        }
        return "";
      },
      subTitle: "Segunda variable (si aplica)",
    },
    {
      label: "Clase de Regla",
      render: (manifest: ValidationRule | ComparisonRule | HistoricalRule | CausalityRule) => {
        if ("ruleType" in manifest) {
          return <span className="text-sm">{ValidationRuleTypeEnum[manifest.ruleType]}</span>;
        } else if ("comparisonType" in manifest) {
          return <span className="text-sm">{ComparisonTypeEnum[manifest.comparisonType]}</span>;
        }
        return "";
      },
      subTitle: "Clasificación de la regla",
    },
    {
      label: "Valor",
      render: (manifest: ValidationRule | ComparisonRule | HistoricalRule | CausalityRule) => {
        if ("value" in manifest) {
          return <span className="text-sm">{resolvedValues[manifest.id] || manifest.value}</span>;
        }
        if ("secondValue" in manifest) {
          return <span className="text-sm">{resolvedValues[manifest.id] || manifest.secondValue}</span>;
        }
        return "";
      },
      subTitle: "Valor de la regla",
    },
    {
      label: "Descripción",
      render: (manifest: ValidationRule | ComparisonRule | HistoricalRule | CausalityRule) => (
        <span className="text-sm text-gray-700">{manifest.errorMessage || ""}</span>
      ),
      subTitle: "Descripción de la regla",
    },
    {
      label: "Fases Aplicables",
      render: (manifest: ValidationRule | ComparisonRule | HistoricalRule | CausalityRule) => {
        const ids = (manifest as any).applicablePhaseIds as number[];
        if (!ids || ids.length === 0) return <span className="text-xs text-gray-500">Todas</span>;
        return (
          <span className="text-xs">
            {ids.map((id) => `F${PhaseDictionary[id.toString()] || id}`).join(", ")}
          </span>
        );
      },
      subTitle: "Fases donde aplica la regla",
    },
    {
      label: "Acciones",
      render: (manifest: ValidationRule | ComparisonRule | HistoricalRule | CausalityRule) => (
        <div className="flex space-x-2">
          {actionLoading === manifest.id ? (
            <div className="animate-spin h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full"></div>
          ) : (
            <>
              <button
                onClick={() => handleEditRule(manifest)}
                className="text-blue-500 hover:text-blue-700 bg-blue-100 p-2 rounded-full hover:bg-blue-200 transition-colors"
                aria-label="Editar"
                title="Editar regla"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536M9 11l6-6 3 3-6 6H9v-3z"
                  />
                </svg>
              </button>

              <button
                onClick={() => handleDuplicateRule(manifest)}
                className="text-green-500 hover:text-green-700 bg-green-100 p-2 rounded-full hover:bg-green-200 transition-colors"
                aria-label="Duplicar"
                title="Duplicar regla"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>

              <button
                onClick={() => handleDeleteClick(manifest.id, manifest.type)}
                className="text-red-500 hover:text-red-700 bg-red-100 p-2 rounded-full hover:bg-red-200 transition-colors"
                aria-label="Eliminar"
                title="Eliminar regla"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </>
          )}
        </div>
      ),
      subTitle: "Editar, duplicar o eliminar",
    },
  ], [manifestNames, resolvedValues, actionLoading, handleToggleRule, handleEditRule, handleDuplicateRule, handleDeleteClick]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        <span className="ml-3 text-lg">Cargando reglas...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 p-8">
        <p className="text-xl font-semibold">⚠️ {error}</p>
        <button 
          onClick={fetchRules}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Inputs de búsqueda */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <input
          type="text"
          placeholder="🔍 Buscar por Tipo (Validation, Comparison...)"
          value={searchType}
          onChange={(e) => setSearchType(e.target.value)}
          className="border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
        />
        <input
          type="text"
          placeholder="🔍 Buscar por Manifestación"
          value={searchManifestation}
          onChange={(e) => setSearchManifestation(e.target.value)}
          className="border border-gray-300 rounded-lg p-3 col-span-1 md:col-span-3 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
        />
      </div>

      <div className="mb-2 text-sm text-gray-600">
        Mostrando {filteredRules.length} de {rules.length} reglas
      </div>

      <CommonTable
        data={filteredRules}
        configs={ruletableConfig}
      />

      {/* Botón Run All Rules y Job Status */}
      <div className="mt-6 flex justify-end items-center space-x-3">
        {runningAll && (
          <div className="animate-spin h-6 w-6 border-4 border-gray-300 border-t-blue-600 rounded-full"></div>
        )}
        
        {!jobStatus && (
          <button
            onClick={handleRunAll}
            className={`px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg transition-all transform hover:scale-105 ${
              runningAll ? "opacity-70 cursor-not-allowed" : ""
            }`}
            disabled={runningAll}
          >
            🚀 Ejecutar Todas las Reglas
          </button>
        )}

        {jobStatus && (
          <div className="w-full md:w-96 rounded-lg border-2 p-4 bg-gradient-to-br from-gray-50 to-white shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                {(jobStatus === "queued" || jobStatus === "running") && (
                  <div className="animate-spin h-5 w-5 border-3 border-blue-500 border-t-transparent rounded-full" />
                )}
                {jobStatus === "succeeded" && <span className="text-2xl">✅</span>}
                {jobStatus === "failed" && <span className="text-2xl">❌</span>}
                {jobStatus === "canceled" && <span className="text-2xl">⚠️</span>}
                <span className="text-sm font-bold uppercase">
                  {jobStatus === "queued" && "En Cola"}
                  {jobStatus === "running" && "Procesando"}
                  {jobStatus === "succeeded" && "Completado"}
                  {jobStatus === "failed" && "Error"}
                  {jobStatus === "canceled" && "Cancelado"}
                </span>
              </div>
              {(jobStatus === "succeeded" || jobStatus === "failed" || jobStatus === "canceled") && (
                <button
                  onClick={handleClearJobStatus}
                  className="text-xs text-gray-500 hover:text-gray-700"
                  title="Cerrar"
                >
                  ✖️
                </button>
              )}
            </div>

            <div className="text-xs text-gray-600 mb-2 min-h-[20px]">
              {jobMessage || "Preparando..."}
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${
                  jobStatus === "succeeded"
                    ? "bg-green-500"
                    : jobStatus === "failed"
                    ? "bg-red-500"
                    : "bg-blue-500"
                }`}
                style={{ width: `${jobProgress ?? 0}%` }}
              />
            </div>

            {/* Métricas y acciones */}
            {(jobStatus === "running" || jobStatus === "queued") && (
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-gray-600 font-mono">
                  {jobProgress ?? 0}% • Gen: {jobMetrics.generated ?? "-"} • Ins: {jobMetrics.inserted ?? "-"}
                </span>
                <button
                  onClick={handleCancelJob}
                  className="px-3 py-1 text-xs rounded-lg bg-red-100 text-red-700 hover:bg-red-200 font-medium transition-colors"
                >
                  Cancelar
                </button>
              </div>
            )}

            {jobStatus === "succeeded" && (
              <div className="mt-3 text-xs text-green-700 font-medium">
                ✅ Generados: {jobMetrics.generated ?? "-"} • Insertados: {jobMetrics.inserted ?? "-"}
              </div>
            )}

            {jobStatus === "failed" && (
              <div className="mt-3 text-xs text-red-700">
                ❌ {jobMessage || "Error en el procesamiento"}
              </div>
            )}
          </div>
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4 text-red-600">⚠️ Confirmar Eliminación</h3>
            <p className="text-gray-700 mb-6">
              ¿Estás seguro de que deseas eliminar esta regla? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edición */}
      {editRuleModalOpen && ruleToEdit && (
        <EditRuleModal
          rule={ruleToEdit}
          onClose={() => {
            setEditRuleModalOpen(false);
            setRuleToEdit(null);
          }}
          onSave={handleSaveRule}
          manifestNames={manifestNames}
          responseDescriptions={responseDescriptions}
          idManResponseAvailable={idManResponseAvailable}
        />
      )}
    </div>
  );
};

export default AllRules;